import React, { useState, useEffect, useMemo } from 'react';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { TNavigationStackWithParams } from 'app/types';
import { MainLayout } from '@shared/components/md-design-system/Layout/MainLayout';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { View, Text } from 'react-native';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { Button } from '@shared/components/md-design-system/Button/Button';
import { Head } from '@shared/components/head';
import { useFetch, usePost } from 'app/queryProvider';
import { ToastNotifications } from 'app/services/inAppToastNotifications';
import { canJoinTelevisit } from '@shared/utils/televisit';
import { useAuth } from 'app/auth/hooks/useAuth';
import { ACTIVITY_STATUS } from '../constants';
import { APPOINTMENT_TYPE, TAppointmentType } from '../constants/appointmentModality';

const AppointmentHome = () => {
    const route = useRoute<RouteProp<TNavigationStackWithParams<{
        appointmentId: string;
        appointmentType?: TAppointmentType;
        professional?: {
            fullName?: string;
            title?: string;
        };
    }>, 'AppointmentScreen'>>();
    const navigation = useNavigation<NativeStackNavigationProp<TNavigationStackWithParams<undefined>>>();
    const { t } = dynamicTranslations();
    const { user } = useAuth();

    const appointmentId = route.params?.appointmentId;
    const appointmentType = route.params?.appointmentType ?? APPOINTMENT_TYPE.TELEVISIT;
    const professionalParam = route.params?.professional;
    const isTeleassistance = appointmentType === APPOINTMENT_TYPE.TELEASSISTANCE;
    const [canConnect, setCanConnect] = useState(true);

    const { data: appointmentResponse, isFetching, refetch } = useFetch<any>({
        url: `/${appointmentId}`,
        queryKey: [["appointment", appointmentId]],
        resource: { name: "appointments" },
        options: {
            enabled: !!appointmentId
        }
    });

    const { mutate: startAppointment, isPending: isStarting } = usePost<undefined, any>({
        options: {
            onSuccess: (data) => {
                refetch();
                if (data?.roomUrl) {
                    const displayName = user ? `${user.given_name} ${user.family_name}` : undefined;
                    (navigation as any).navigate('WherebyRoomScreen', {
                        roomUrl: data.roomUrl,
                        displayName,
                        professional: data.professional || professionalParam,
                        appointmentType,
                    });
                }
            },
            onError: () => {
                ToastNotifications.displayErrorNotification(
                    isTeleassistance
                        ? t('teleassistance.waiting.errors.notStarted')
                        : t('televisits.errors.notStarted')
                );
            }
        }
    });

    const appointmentTime = useMemo(() => appointmentResponse?.time, [appointmentResponse?.time]);

    useEffect(() => {
        if (!appointmentTime) {
            return;
        }

        const checkTime = () => {
            const canJoin = canJoinTelevisit(appointmentTime);
            setCanConnect(canJoin);
        };

        checkTime();
        const interval = setInterval(checkTime, 60000);

        return () => clearInterval(interval);
    }, [appointmentTime]);

    const handleGoToHome = () => {
        navigation.goBack();
    };

    const handleConnect = () => {
        const displayName = user ? `${user.given_name} ${user.family_name}` : undefined;

        if (!appointmentResponse?.roomUrl) {
            startAppointment({
                resource: { name: "appointments", id: appointmentId },
                url: "/start",
                data: undefined as any
            });
        } else {
            (navigation as any).navigate('WherebyRoomScreen', {
                roomUrl: appointmentResponse.roomUrl,
                displayName,
                professional: appointmentResponse.professional || professionalParam,
                appointmentType,
            });
        }
    };

    const isLoading = isFetching || isStarting;
    const isStatusDisabled = [ACTIVITY_STATUS.DISABLED, ACTIVITY_STATUS.COMPLETED].includes(appointmentResponse?.status);

    return (
        <MainLayout navigation={handleGoToHome} isLoading={isLoading}>
            <View className="flex-1 justify-center items-center px-6">
                <Head type="h1" className="text-center mb-4">
                    {canConnect
                        ? (isTeleassistance ? t('teleassistance.waiting.title') : t('televisits.title'))
                        : (isTeleassistance ? t('teleassistance.waiting.tooEarly.title') : t('televisits.tooEarly.title'))
                    }
                </Head>

                {!canConnect && (
                    <Text className="text-center text-gray-600 text-lg">
                        {isTeleassistance ? t('teleassistance.waiting.tooEarly.subtitle') : t('televisits.tooEarly.subtitle')}
                    </Text>
                )}

                {canConnect && (
                    <Button
                        onPress={handleConnect}
                        disabled={isLoading || isStatusDisabled}
                        variant='tiffany'
                        size='large'
                        icon='video-camera'
                    >
                        <Text className="text-base font-bold text-lg">
                            {isLoading ? t('commons.loading') : (isTeleassistance ? t('teleassistance.waiting.connect') : t('televisits.connect'))}
                        </Text>
                    </Button>
                )}
            </View>
        </MainLayout>
    );
};

export default AppointmentHome;
